import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonIcon from '@mui/icons-material/Person'
import SendIcon from '@mui/icons-material/Send'
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSherpaChat } from 'api/aws/strategy-builder/useStrategyBuilder'
import type { AiRecommendation, StrategyItem } from './types'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface StrategyConfig {
  clientName: string
  clientSize: string
  serviceStage: string
  targetPointBudget: number
  strategyGoal?: string
  focusAreas?: string[]
  sherpaNotes?: string
  months?: string[]
}

export interface AISherpaDialogProps {
  open: boolean
  onClose: () => void
  strategyId: string | null
  cartItems: StrategyItem[]
  config: StrategyConfig
  recommendations: AiRecommendation[]
}

// ── Suggested Questions ───────────────────────────────────────────────────────

const SUGGESTED_QUESTIONS = [
  'Review my strategy',
  'Optimize for budget',
  'What am I missing?',
  'Suggest alternatives',
]

// ── Component ─────────────────────────────────────────────────────────────────

const AISherpaDialog = ({ open, onClose, strategyId, cartItems, config, recommendations }: AISherpaDialogProps) => {
  const theme = useTheme()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const sherpaMutation = useSherpaChat()

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || !strategyId || isTyping) return

      const userMessage: ChatMessage = { role: 'user', content: text.trim() }
      const updatedMessages = [...messages, userMessage]
      setMessages(updatedMessages)
      setInput('')
      setIsTyping(true)

      try {
        const result = await sherpaMutation.mutateAsync({
          strategyId,
          message: text.trim(),
          history: updatedMessages,
          cartItems,
          config,
          recommendations,
        })

        setMessages(prev => [...prev, { role: 'assistant', content: result.response }])
      } catch {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
        ])
      } finally {
        setIsTyping(false)
      }
    },
    [strategyId, messages, isTyping, sherpaMutation, cartItems, config, recommendations]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage(input)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          height: '70vh',
          maxHeight: 600,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          py: 1.5,
        }}
      >
        <AutoAwesomeIcon sx={{ color: theme.palette.primary.main }} />
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          AI Sherpa
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {cartItems.length} items in cart
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
        }}
      >
        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {/* Welcome message if no messages */}
          {messages.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AutoAwesomeIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Strategy Advisor
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                Ask me anything about your strategy for {config.clientName || 'this client'}.
              </Typography>

              {/* Suggested questions */}
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                {SUGGESTED_QUESTIONS.map(q => (
                  <Chip
                    key={q}
                    label={q}
                    variant="outlined"
                    size="small"
                    onClick={() => sendMessage(q)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: theme.palette.action.hover },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Chat messages */}
          {messages.map((msg, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                gap: 1,
              }}
            >
              {msg.role === 'assistant' && (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
              )}
              <Box
                sx={{
                  maxWidth: '80%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor:
                    msg.role === 'user'
                      ? theme.palette.primary.main
                      : theme.palette.mode === 'dark'
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                  color:
                    msg.role === 'user'
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                >
                  {msg.content}
                </Typography>
              </Box>
              {msg.role === 'user' && (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: theme.palette.grey[600],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  <PersonIcon sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
              )}
            </Box>
          ))}

          {/* Typing indicator */}
          {isTyping && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 16, color: '#fff' }} />
              </Box>
              <Box
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={14} />
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Thinking...
                </Typography>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <TextField
            inputRef={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your strategy..."
            fullWidth
            size="small"
            multiline
            maxRows={3}
            disabled={isTyping || !strategyId}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          <IconButton
            color="primary"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isTyping || !strategyId}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: '#fff',
              '&:hover': { bgcolor: theme.palette.primary.dark },
              '&.Mui-disabled': { bgcolor: theme.palette.action.disabledBackground },
              width: 36,
              height: 36,
            }}
          >
            <SendIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default AISherpaDialog
